import React, { useState } from 'react';
import { X, Globe, Brain, Code, Plus, ChevronRight } from 'lucide-react';

interface N8nNodePanelProps {
  onClose: () => void;
}

interface AIProviderOption {
  id: string;
  name: string;
  description: string;
  icon: string;
}

const N8nNodePanel: React.FC<N8nNodePanelProps> = ({ onClose }) => {
  const [showAIProviders, setShowAIProviders] = useState(false);
  const [showHTTPOptions, setShowHTTPOptions] = useState(false);
  const [showEVMOptions, setShowEVMOptions] = useState(false);
  
  const aiProviders: AIProviderOption[] = [
    { id: 'openai', name: 'OpenAI GPT', description: 'GPT-4, GPT-3.5 Turbo', icon: '🤖' },
    { id: 'anthropic', name: 'Anthropic Claude', description: 'Claude 3.5 Sonnet, Haiku', icon: '🧠' },
    { id: 'google', name: 'Google Gemini', description: 'Gemini Pro, Flash', icon: '💎' },
    { id: 'mistral', name: 'Mistral AI', description: 'Mistral Large, Small', icon: '🌪️' },
    { id: 'cohere', name: 'Cohere', description: 'Command R+, Light', icon: '🔗' },
    { id: 'perplexity', name: 'Perplexity AI', description: 'Sonar with web search', icon: '🔍' }
  ];
  const nodeTypes = [
    {
      id: 'HTTP',
      name: 'HTTP Request',
      icon: Globe,
      color: 'bg-green-100 text-green-700',
      description: 'Make HTTP requests to APIs'
    },
    {
      id: 'AI',
      name: 'AI Assistant',
      icon: Brain,
      color: 'bg-blue-100 text-blue-700',
      description: 'AI-powered processing'
    },
    {
      id: 'EVM',
      name: 'EVM Operations',
      icon: Code,
      color: 'bg-orange-100 text-orange-700',
      description: 'Blockchain read/write operations'
    }
  ];

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
    // Prevent layout shift during drag
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.style.opacity = '0.5';
    }
  };

  const onDragEnd = (event: React.DragEvent) => {
    // Restore opacity after drag
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.style.opacity = '1';
    }
  };

  const onAIProviderDragStart = (event: React.DragEvent, providerId: string) => {
    event.dataTransfer.setData('application/reactflow', `AI:${providerId}`);
    event.dataTransfer.effectAllowed = 'move';
    // Prevent layout shift during drag
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.style.opacity = '0.5';
    }
  };

  const handleAINodeClick = () => {
    setShowAIProviders(!showAIProviders);
  };

  return (
    <div className="absolute top-24 left-[280px] w-72 h-[500px] bg-n8n-dark-800 border border-n8n-dark-600 rounded-xl shadow-2xl z-40 flex flex-col overflow-hidden">
      {/* Compact Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-n8n-dark-600">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-n8n-primary-400 rounded-full animate-pulse"></div>
          <h2 className="text-sm font-bold text-n8n-dark-50">Add Nodes</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-n8n-dark-600/50 rounded-lg transition-all duration-200 group"
        >
          <X size={16} className="text-n8n-dark-400 group-hover:text-n8n-dark-200" />
        </button>
      </div>

      {/* Compact Search bar */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-n8n-dark-700">
        <input
          type="text"
          placeholder="Search..."
          className="w-full px-3 py-1.5 text-sm bg-n8n-dark-700 border border-n8n-dark-600 text-n8n-dark-200 placeholder-n8n-dark-400 rounded-lg focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 outline-none transition-all duration-200"
        />
      </div>

      {/* Compact Scrollable Node categories */}
      <div className="flex-1 overflow-y-auto px-3 py-2 scroll-smooth scrollbar-thin scrollbar-thumb-n8n-dark-600 scrollbar-track-transparent hover:scrollbar-thumb-n8n-dark-500" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4a5568 transparent' }}>
        <div className="text-xs font-semibold text-n8n-dark-400 mb-2 px-1">NODES</div>
        <div className="space-y-2">
          {nodeTypes.map((node) => {
            const Icon = node.icon;
            
            if (node.id === 'AI') {
              return (
                <div key={node.id}>
                  <div
                    onClick={handleAINodeClick}
                    className="group p-2 border border-n8n-dark-600 bg-n8n-dark-700 rounded-lg hover:border-n8n-primary-500 hover:bg-n8n-dark-600 cursor-pointer transition-all duration-200"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-n8n-dark-600 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                        <Icon size={16} className="text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-n8n-dark-100 text-xs truncate">{node.name}</div>
                        <div className="text-[10px] text-n8n-dark-500 truncate">{node.description}</div>
                      </div>
                      <ChevronRight 
                        size={14} 
                        className={`text-n8n-dark-400 transition-transform duration-200 flex-shrink-0 ${
                          showAIProviders ? 'rotate-90' : ''
                        }`} 
                      />
                    </div>
                  </div>
                  
                  {showAIProviders && (
                    <div className="ml-3 mt-1.5 space-y-1 border-l-2 border-n8n-dark-600 pl-2">
                      {aiProviders.map((provider) => (
                        <div
                          key={provider.id}
                          draggable
                          onDragStart={(event) => onAIProviderDragStart(event, provider.id)}
                          onDragEnd={onDragEnd}
                          className="group p-1.5 border border-n8n-dark-600 bg-n8n-dark-700 rounded-md hover:bg-n8n-dark-600 hover:border-n8n-primary-500 cursor-move transition-all duration-150"
                        >
                          <div className="flex items-center space-x-1.5">
                            <div className="w-6 h-6 rounded-md bg-n8n-dark-600 flex items-center justify-center text-xs group-hover:bg-blue-500/20 transition-colors">
                              {provider.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-n8n-dark-100 text-[10px] truncate">{provider.name}</div>
                              <div className="text-[9px] text-n8n-dark-500 truncate">{provider.description}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            if (node.id === 'HTTP') {
              return (
                <div key={node.id}>
                  <div
                    onClick={() => setShowHTTPOptions(!showHTTPOptions)}
                    className="group p-2 border border-n8n-dark-600 bg-n8n-dark-700 rounded-lg hover:border-n8n-success-500 hover:bg-n8n-dark-600 cursor-pointer transition-all duration-200"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-n8n-dark-600 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                        <node.icon size={16} className="text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-n8n-dark-100 text-xs truncate">{node.name}</div>
                        <div className="text-[10px] text-n8n-dark-500 truncate">{node.description}</div>
                      </div>
                      <ChevronRight 
                        size={14} 
                        className={`text-n8n-dark-400 transition-transform duration-200 flex-shrink-0 ${
                          showHTTPOptions ? 'rotate-90' : ''
                        }`} 
                      />
                    </div>
                  </div>

                  {showHTTPOptions && (
                    <div className="ml-3 mt-1.5 space-y-1 border-l-2 border-n8n-dark-600 pl-2">
                      <div
                        key="http-get"
                        draggable
                        onDragStart={(event) => onDragStart(event, 'API_GET')}
                        onDragEnd={onDragEnd}
                        className="group p-1.5 border border-n8n-dark-600 bg-n8n-dark-700 rounded-md hover:bg-n8n-dark-600 hover:border-n8n-success-500 cursor-move transition-all duration-150"
                      >
                        <div className="flex items-center space-x-1.5">
                          <div className="w-6 h-6 rounded-md bg-n8n-dark-600 flex items-center justify-center text-[9px] font-bold text-green-400 group-hover:bg-green-500/20 transition-colors">
                            GET
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-n8n-dark-100 text-[10px] truncate">HTTP GET</div>
                            <div className="text-[9px] text-n8n-dark-500 truncate">url + method</div>
                          </div>
                        </div>
                      </div>
                      <div
                        key="http-post"
                        draggable
                        onDragStart={(event) => onDragStart(event, 'API_POST')}
                        onDragEnd={onDragEnd}
                        className="group p-1.5 border border-n8n-dark-600 bg-n8n-dark-700 rounded-md hover:bg-n8n-dark-600 hover:border-n8n-success-500 cursor-move transition-all duration-150"
                      >
                        <div className="flex items-center space-x-1.5">
                          <div className="w-6 h-6 rounded-md bg-n8n-dark-600 flex items-center justify-center text-[9px] font-bold text-green-400 group-hover:bg-green-500/20 transition-colors">
                            POST
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-n8n-dark-100 text-[10px] truncate">HTTP POST</div>
                            <div className="text-[9px] text-n8n-dark-500 truncate">headers, body, timeout</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            if (node.id === 'EVM') {
              return (
                <div key={node.id}>
                  <div
                    onClick={() => setShowEVMOptions(!showEVMOptions)}
                    className="group p-2 border border-n8n-dark-600 bg-n8n-dark-700 rounded-lg hover:border-orange-500 hover:bg-n8n-dark-600 cursor-pointer transition-all duration-200"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-n8n-dark-600 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                        <node.icon size={16} className="text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-n8n-dark-100 text-xs truncate">{node.name}</div>
                        <div className="text-[10px] text-n8n-dark-500 truncate">{node.description}</div>
                      </div>
                      <ChevronRight 
                        size={14} 
                        className={`text-n8n-dark-400 transition-transform duration-200 flex-shrink-0 ${
                          showEVMOptions ? 'rotate-90' : ''
                        }`} 
                      />
                    </div>
                  </div>

                  {showEVMOptions && (
                    <div className="ml-3 mt-1.5 space-y-1 border-l-2 border-n8n-dark-600 pl-2">
                      <div
                        key="evm-read"
                        draggable
                        onDragStart={(event) => onDragStart(event, 'EVM_READ')}
                        onDragEnd={onDragEnd}
                        className="group p-1.5 border border-n8n-dark-600 bg-n8n-dark-700 rounded-md hover:bg-n8n-dark-600 hover:border-orange-500 cursor-move transition-all duration-150"
                      >
                        <div className="flex items-center space-x-1.5">
                          <div className="w-6 h-6 rounded-md bg-n8n-dark-600 flex items-center justify-center text-[9px] font-bold text-orange-400 group-hover:bg-orange-500/20 transition-colors">
                            READ
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-n8n-dark-100 text-[10px] truncate">EVM Read</div>
                            <div className="text-[9px] text-n8n-dark-500 truncate">Call contract functions</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <div
                key={node.id}
                draggable
                onDragStart={(event) => onDragStart(event, node.id)}
                onDragEnd={onDragEnd}
                className="group p-2 border border-n8n-dark-600 bg-n8n-dark-700 rounded-lg hover:border-n8n-primary-500 hover:bg-n8n-dark-600 cursor-move transition-all duration-200"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-n8n-dark-600 flex items-center justify-center group-hover:bg-gray-500/20 transition-colors">
                    <Icon size={16} className="text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-n8n-dark-100 text-xs truncate">{node.name}</div>
                    <div className="text-[10px] text-n8n-dark-500 truncate">{node.description}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Compact Help text */}
      <div className="flex-shrink-0 px-3 py-2 text-[10px] text-n8n-dark-500 text-center border-t border-n8n-dark-700">
        <span className="flex items-center justify-center space-x-1">
          <span>✨</span>
          <span>Drag & drop to add nodes</span>
        </span>
      </div>
    </div>
  );
};

export default N8nNodePanel;